import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useModelKPICalculator } from "@services/api";
import { ModelKPICalculatorMedia } from "@services/api/types";
import { toLocaleString } from "@utils/numbers";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

const toTableRow = (
  element: ModelKPICalculatorMedia,
  highlight: boolean = false
): JSX.Element => {
  const fontWeight = highlight ? "bold" : "normal";
  return (
    <StyledTableRow
      key={element.media_name}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      <StyledTableCell component="th" scope="row" sx={{ fontWeight }}>
        {element.media_name}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.incremental_kpi, 0)}
      </StyledTableCell>
      {/* <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.incremental_sales, 0)}
      </StyledTableCell> */}
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.pct_investment)}%
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.pct_incremental_kpi)}%
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.media_effectiveness)}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.investment, 0)}
      </StyledTableCell>

      {/* <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.incremental_sales_per_1000, 0)}
      </StyledTableCell> */}
      <StyledTableCell align="center" sx={{ fontWeight }}>
        {toLocaleString(element.media_spend_kpi, 2)}
      </StyledTableCell>
    </StyledTableRow>
  );
};

const ModelKPICalculator = () => {
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const iconVisibility = isTablet ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "justify-center";

  const { data, isFetching: isLoading } = useModelKPICalculator(
    currentClient.model.id
  );

  const tableData = useMemo(() => {
    const mediaData = (data?.media || []).map((mediaData) => {
      return toTableRow(mediaData);
    });
    if (data) {
      return [...mediaData, toTableRow(data.total, true)];
    }
    return mediaData;
  }, [data]);

  return (
    <Box
      className="w-full pt-1 pl-4 pr-4"
      sx={{
        height: "100%"
      }}
    >
      <Paper elevation={1} sx={{ height: "100%" }}>
        <Typography variant="h6" component="div" className="pt-4 pl-6">
          {t("mmmodel.kpi_calculator.title")}
        </Typography>
        <Box className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}>
          <SwipeIcon
            sx={{
              bottom: 12,
              opacity: 0.4,
              left: "50%",
              transform: "translate(750%, 0%)",
              color: theme.palette.primary.main
            }}
            fontSize="small"
          />
        </Box>
        <TableContainer
          className="p-4"
          sx={{
            overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
            scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
            "&::-webkit-scrollbar": {
              display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
            }
          }}
        >
          <Table className="w-full" aria-label="kpi calculator table">
            <TableHead>
              <TableRow>
                <StyledTableCell>
                  {t("mmmodel.kpi_calculator.media")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.incremental_inflow", {
                    kpi: currentClient.client.kpi_label
                  })}
                </StyledTableCell>
                {/* <StyledTableCell align="center">
                    {t("mmmodel.kpi_calculator.incremental_sales")}
                  </StyledTableCell> */}
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.media_spend")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.contribution")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.media_effectiveness")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.media_spend_total", {
                    currency: currentClient.client.currency
                  })}
                </StyledTableCell>
                {/*  <StyledTableCell align="center">
                    {t("mmmodel.kpi_calculator.sales_1k")}
                  </StyledTableCell> */}
                <StyledTableCell align="center">
                  {t("mmmodel.kpi_calculator.media_spend_kpi", {
                    currency: currentClient.client.currency,
                    kpi: currentClient.client.kpi_label
                  })}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRowsSkeleton
                  id="kpi-calculator-skeleton"
                  numberOfRows={6}
                  columns={6}
                />
              ) : (
                tableData
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
export default ModelKPICalculator;
